import { Injectable } from '@angular/core';
import { COUNTRY_CODE } from '@cemex-core/cmx-api';
import { State } from '@cemex/cmx-client-layout';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take, withLatestFrom } from 'rxjs/operators';
import { SetActiveStep, UpdateStepsAction } from '../../order-builder/actions/stepper-actions';
import { getRouteSelectionFields } from '../../order-builder/selectors/order-strategy.selector';
import { PRODUCT_LINE_CODE } from '../constants/product-lines-codes';
import { SHIPPING_CONDITION_CODE } from '../constants/shipping-conditions-codes';
import { defaultStepsRoutes, repo } from '../repositories/routes.repo';
import { _safe } from '../utils/safe-get.method';
import { StepRoute } from './../models/step-route.model';
import { NewOrderFlowService } from '../modules/new-order-flow/new-order-flow.service';
import { ORDER_BUILDER_ROUTES } from '../constants/order-builder-routes';

export interface RouteSolverResponse {
  previous: StepRoute;
  next: StepRoute;
  first: StepRoute;
  last: StepRoute;
}

@Injectable()
export class OrderBuilderRouteSolver {
  constructor(
    private store: Store<State>,
    private newOrderFlowService: NewOrderFlowService,
  ) {}

  public solveRoute(url: string, forcedSteps?: StepRoute[], updateSteps = true): Observable<RouteSolverResponse> {
    return this.store.select(getRouteSelectionFields).pipe(
      withLatestFrom(this.newOrderFlowService.isNewOrderFlowEnabled$()),
      take(1),
      map(([{ customer, businessLineState }, isNewOrderFlowEnabled]) => {
        const countryCode = customer && customer.countryCode;
        const productLine = _safe(() => businessLineState.businessLine.selected.productTypeCode as PRODUCT_LINE_CODE);
        const shippingCondition = _safe(
          () => businessLineState.shippingCondition.selected.shippingConditionCode,
        ) as SHIPPING_CONDITION_CODE;
        return this.route(
          url,
          countryCode,
          productLine,
          shippingCondition,
          isNewOrderFlowEnabled,
          forcedSteps,
          updateSteps,
        );
      }),
    );
  }

  private route(
    url: string,
    countryCode: COUNTRY_CODE,
    productLine: PRODUCT_LINE_CODE,
    shippingCondition: SHIPPING_CONDITION_CODE,
    isNewOrderFlowEnabled: boolean,
    forcedSteps?: StepRoute[],
    updateSteps?: boolean,
  ): RouteSolverResponse {
    const routes =
      forcedSteps ||
      this.getRepoRoutes(countryCode, productLine, shippingCondition).filter((route) => {
        if (isNewOrderFlowEnabled) {
          return route.route !== ORDER_BUILDER_ROUTES.BusinessLine;
        }

        return true;
      });

    const position = routes.findIndex((step) => step.route === url);

    updateSteps && this.updateSteps(position, routes);

    const previous = this.previousRoute(position, routes);
    const next = this.nextRoute(position, routes);
    return { previous, next, first: routes[0], last: routes[routes.length - 1] };
  }

  public getRepoRoutes(
    countryCode: COUNTRY_CODE,
    productLine: PRODUCT_LINE_CODE,
    shippingCondition: SHIPPING_CONDITION_CODE,
  ): StepRoute[] {
    return _safe(() => repo[countryCode][productLine][shippingCondition], defaultStepsRoutes);
  }

  private previousRoute(position: number, routes: StepRoute[]) {
    const isFirst = position === 0;
    return isFirst ? routes[0] : routes[position - 1];
  }

  private nextRoute(position: number, routes: StepRoute[]) {
    const isLast = position === routes.length - 1;
    return isLast ? routes[position] : routes[position + 1];
  }

  private updateSteps(position: number, routes: StepRoute[]) {
    this.store.dispatch(new UpdateStepsAction(routes));
    this.store.dispatch(new SetActiveStep(position));
  }
}
